import { useState, useEffect } from "react";
import validateField from "../utils/substitutionFormValidation";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import { useInputState } from "../hooks/useInputState";
import { useFormValidation } from "../hooks/useFormValidation";
import { useFormSubmit } from "../hooks/useFormSubmit";
import { RacketInput } from "./index";
import { useParenHighlight } from "../hooks/useParenHighlight";

export default function Substitution({
  show,
  handleClose,
  racketRuleFields,
  handleSubstitution,
  errors,
  initialRule,
  initialSubstitution
}) {
  const initialValues = {
    substitution: initialSubstitution || "",
    rule: initialRule || ""
  };

  const [formValues, handleChange] = useInputState(initialValues);
  const [validationMessages, handleBlur, setAllTouched, isFormValid] =
    useFormValidation(formValues, validateField);
  const [validated, setValidated] = useState(false);

  const {
    highlightPositions: substitutionHighlight,
    inputRef: substitutionRef,
    handleKeyUp: handleSubstitutionKeyUp,
    handleSelect: handleSubstitutionSelect
  } = useParenHighlight(formValues.substitution);

  const handleSubstitutionSubmit = async () => {
    // Clear previous errors before attempting submission
    // (errors prop will be updated by parent with new errors if validation fails)
    const valid = await handleSubstitution(formValues);

    if (!valid) {
      setValidated(false);
    }
  };

  const { handleSubmit } = useFormSubmit(
    isFormValid,
    setValidated,
    setAllTouched,
    handleSubstitutionSubmit
  );

  useEffect(() => {
    // Fall back to the last proof line's rule when no rule was pre-supplied.
    if (initialRule && initialRule.trim().length > 0) {
      return;
    }
    const undeletedProofLines = racketRuleFields.filter((line) => {
      return !line.deleted;
    });
    const lastUnDeletedFieldIndex = undeletedProofLines.length - 1;

    if (undeletedProofLines.length > 0) {
      const ruleValue = undeletedProofLines[lastUnDeletedFieldIndex].rule;
      if (ruleValue.trim().length > 0) {
        handleChange({ target: { name: "rule", value: ruleValue } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Form validated={validated} onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Substitution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            {Array.isArray(errors) && errors.length > 0 && (
              <Alert variant="danger" className="scroll-error">
                {errors.map((error, index) => (
                  <span key={`racket-error-${index}`}>{error}</span>
                ))}
              </Alert>
            )}
          </Row>
          <Row>
            <Form.Group as={Col} md="8">
              <label>Substitution</label>
              <RacketInput
                name="substitution"
                type="text"
                placeholder="Enter the substitution"
                value={formValues.substitution}
                onBlur={() => handleBlur("substitution")}
                onChange={handleChange}
                onKeyUp={handleSubstitutionKeyUp}
                onClick={handleSubstitutionSelect}
                isInvalid={validationMessages.substitution}
                required
                highlightPositions={substitutionHighlight}
                ref={substitutionRef}
              />
              <Form.Control.Feedback type="invalid">
                {validationMessages.substitution}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="4">
              <Form.Floating className="mb-3">
                <Form.Control
                  name="rule"
                  type="text"
                  placeholder="Rule"
                  value={formValues.rule}
                  onBlur={() => handleBlur("rule")}
                  onChange={handleChange}
                  isInvalid={validationMessages.rule}
                  required
                />
                <label>Rule</label>
                <Form.Control.Feedback type="invalid">
                  {validationMessages.rule}
                </Form.Control.Feedback>
              </Form.Floating>
            </Form.Group>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary">Submit</button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
